/*global ga YT */
import {createElement, Component, PropTypes} from 'rlink-react';
import {FormattedMessage, FormattedHTMLMessage} from 'react-intl';
import Main from 'components/main';
import css from 'static/style/dow.less';
import {fromJS} from 'immutable';
import fontAwesome from 'rlink-lesscore/icons/font-awesome.less';
import contentLoader from 'helpers/content-loader';
import {FeaturedStories} from 'rlink-blog/component';

import {DOW, SITE_DESCRIPTION} from 'terms';
import {SKU_GROUPS} from 'region-config';
import DocumentTitle from 'rlink-react-document/DocumentTitle.jsx';
import DocumentOpenGraphProperty from 'rlink-react-document/DocumentOpenGraphProperty.jsx';
import {default as LegacyScrollTransition, LegacyCoveringScrollTransition} from 'rlink-transitions/LegacyScrollTransition.jsx';
import TrackViewOnVisible from 'rlink-telemetry/TrackViewOnVisible.jsx';
import {smoothScrollTo} from 'rlink-utils/scrolling';

import ShopListing from 'rlink-shop/components/ShopListing';
import getUserSelectedCountry from 'rlink-regions/getUserSelectedCountry';
import getCountryFromPreferredLanguages from 'rlink-regions/getCountryFromPreferredLanguages';
import getPreferredLanguages from 'rlink-regions/getPreferredLanguages';

export default function (params, content) {
  let preferredLanguages = getPreferredLanguages();
  let userCountry = getUserSelectedCountry();
  if (userCountry === null) {
    userCountry = getCountryFromPreferredLanguages(preferredLanguages);
  }

  let items = content.products.data.get('items').toJS().filter(item => {
    if (!item.viewsWhitelist.includes('home')) {
      return false;
    }

    if (!('skuGroups' in item)
      || item.skuGroups.length === 0
      || item.hasRestrictions === false) {
      return true;
    }

    for (let skuGroup of item.skuGroups) {
      if (SKU_GROUPS[skuGroup].includes(userCountry)) {
        return true;
      }
    }

    return false;
  });

  return (<Main view="home" globalSettings={content.globalSettings}>
    <DocumentTitle title={DOW} />
    <DocumentOpenGraphProperty property="url" value="https://www.dawnofwar.com" />
    <DocumentOpenGraphProperty property="title" value={DOW} />
    <DocumentOpenGraphProperty property="description" value={SITE_DESCRIPTION} />
    <DocumentOpenGraphProperty property="image" value={contentLoader('key-art-1080.jpg')} />
    <div key="home">
      <main>
        <SocialLinks />
        <CoverPanel />
        <CTAPanel userCountry={userCountry} params={params} content={content} items={items.filter(a => a.vendor === 'HumbleBundleService')} />
        <NewsPanel articles={content.articles.data.get('items')} />
        <NewsletterCTA />
        <StoryPanel />
        <ElitesPanel />
        <EpicPanel />
        <CampaignPanel />
        <RacesPanel />
        <hr className={css.blank} />
        <DestructiveAbilitiesPanel />
        <CallUpElitesPanel />
        <TrailerPanel />
        <Trailer ref={el => {

          let trailerLaunchers = document.querySelectorAll([
            '.trailer-launcher',
          ]);
          for (let i = 0; i < trailerLaunchers.length; i++) {
            let youtubeVideoId = ('youtubeVideoId' in trailerLaunchers[i].dataset)
              ? trailerLaunchers[i].dataset.youtubeVideoId
              : 'q0XnU0xaqsY';
            trailerLaunchers[i].addEventListener('click', event => el.openModal({videoId: youtubeVideoId, event}));
          }
        }} css={css} />
        <hr className={css.blank} />
        <ShopItemsPanel params={params} content={content} userCountry={userCountry} items={items.filter(a => a.vendor === 'ScalefastService')} />
      </main>
    </div>
  </Main>);
}

function SocialLinks () {
  return <ul className={css["front-page-social-links"]}>
    <li>
      <a target="_blank" href="https://www.facebook.com/DawnOfWar" className={`${fontAwesome['fa']} ${fontAwesome['fa-facebook']}`}></a>
    </li>
    <li>
      <a target="_blank" href="https://twitter.com/dawnofwar" className={`${fontAwesome['fa']} ${fontAwesome['fa-twitter']}`}></a>
    </li>
    <li>
      <a target="_blank" href="https://www.youtube.com/channel/UCWxzyJYKnfcLRel2Io5NP9g" className={`${fontAwesome['fa']} ${fontAwesome['fa-youtube']}`}></a>
    </li>
    <li>
      <a target="_blank" href="https://www.twitch.tv/relicentertainment/profile" className={`${fontAwesome['fa']} ${fontAwesome['fa-twitch']}`}></a>
    </li>
  </ul>;
}

function CTAPanel ({userCountry, params, content, items}) {
  let yDestination = window.innerHeight / 1.25;
  return <section className={css.HomeCTA}>
    <div>
      <h1 dangerouslySetInnerHTML={{__html: content.globalSettings.tagline}} className={css['main-quote']} />
      <ShopListing css={css}
        layoutType={ShopListing.layoutTypes.CTA_OUTSIDE}
        region={(params) ? params.region : ''}
        defaultRatingsBoard={content.globalSettings.defaultRatingsBoard}
        shopItems={fromJS(items)}
        slug={''}
        skuGroups={SKU_GROUPS}
        userCountry={userCountry} />
      <a onClick={() => smoothScrollTo({x: 0, y: yDestination }, 500, (t, b, c, d) => {
        return c*(t/=d)*t + b;
      })} className={css.ScrollIndicator}>
        <span className={`${fontAwesome['fa']} ${fontAwesome['fa-angle-down']} ${fontAwesome['fa-lg']}`} />
      </a>
    </div>
  </section>;
}

function CoverPanel () {
  return (
    <section className={css.cover}>
      <div className={css['panel-bg']}>
        <picture>
          <source srcSet={contentLoader('key-art-320.jpg')} type="image/jpeg" media="(max-height:320px)" />
          <source srcSet={contentLoader('key-art-420.jpg')} type="image/jpeg" media="(min-height:321px) and (max-height:420px)" />
          <source srcSet={contentLoader('key-art-768.jpg')} type="image/jpeg" media="(min-height:421px) and (max-height:768px)" />
          <source srcSet={contentLoader('key-art-1024.jpg')} type="image/jpeg" media="(min-height:769px) and (max-height:1024px)" />
          <source srcSet={contentLoader('key-art-1080.jpg')} type="image/jpeg" media="(min-height:1025px)" />
          <img src={contentLoader('key-art-1080.jpg')} className={css['bg-media']} id="cover_poster" />
        </picture>

        <video
            id="cover_video"
          className={`${css['bg-media']} ${css.autoplay}`}
            loop
            muted
            preload="auto"
            autoPlay>
          <source src={contentLoader('key-art-1080.mp4', 'video')} type="video/mp4" />
        </video>
      </div>
      <div className={css.RadialMask} />
    </section>
  );
}

function ShopItemsPanel ({params, content, userCountry, items}) {
  return (
    <section className={`${css["cover-shop"]} ${css["cover-tagline"]}`}>
      <div className={css["panel-content"]}>
        <ShopListing css={css}
          layoutType={ShopListing.layoutTypes.CTA_OUTSIDE}
          region={(params) ? params.region : ''}
          defaultRatingsBoard={content.globalSettings.defaultRatingsBoard}
          shopItems={fromJS(items)}
          slug={''}
          skuGroups={SKU_GROUPS}
          userCountry={userCountry} />
      </div>
    </section>
  );
}

function NewsPanel ({articles}) {
  return (
    <section className={css['cover-news']}>
      <div className={css['panel-content']}>
        <FeaturedStories
          articleData={articles}
          css={css}
          fontAwesome={fontAwesome} />
      </div>
    </section>
  );
}

function NewsletterCTA () {
  return (
    <section className={css['newsletter-cta']}>
      <div className={css['panel-content']}>
        <hr className={`${css.skull} ${css['fade-out']}`} />
        <h1>
          <FormattedMessage
            id="ui.label.title.SIGN_UP"
            defaultMessage="stay updated"
            description="Heading above the newsletter sign up button." />
        </h1>
        <a target="_blank" rel="noopener" href="https://sso.relic.com/signup?title=dow3&newsletterOnly=1" className={`${css.btn} ${css['btn-secondary']}`}>
          <FormattedMessage
            id="ui.label.button.SIGN_UP"
            defaultMessage="sign up now"
            description="Sign up now button on the home page" />
        </a>
        <hr className={`${css.skull} ${css['fade-out']}`} />
      </div>
    </section>
  );
}

function StoryPanel () {
  return (
    <LegacyCoveringScrollTransition coveringElementClassName={css['newsletter-cta']} css={css}>
      <section className={css['story-panel']}>
        <div className={css.background} />
        <div className={`${css['panel-content']} ${css['sidebar-noclip']}`} id="story">
          <TrackViewOnVisible name="story" />
          <h1 className={css.StoryHeading}>
            <FormattedHTMLMessage
              id="ui.branding.heading.DOW3_STORY_PANEL_ANIMATED_TAGLINE"
              defaultMessage={'<span class="{POWER_CLASSNAME}">Power</span> <span class="{CONNECTY_WORDS_CLASSNAME}">has a</a> <span class="{PRICE_CLASSNAME}">price</a>'}
              description="Animated tagline appearing on the home page. The three different <span>...</span> parts fade-in one after the other and should be of roughly equal size. {POWER_CLASSNAME}, {CONNECTY_WORDS_CLASSNAME} and {PRICE_CLASSNAME} are used to give the different parts their animations and should remain in order."
              values={{
                POWER_CLASSNAME: css.power,
                CONNECTY_WORDS_CLASSNAME: css['connecty-words'],
                PRICE_CLASSNAME: css.price,
              }} />
          </h1>
          <hr className={css.short} />
          <p className={css.choices}>
            <FormattedMessage
              id="ui.branding.copy.DOW3_STORY_PANEL_BODY_FIRST"
              defaultMessage="In Dawn of War III you will have no choice but to face your foes when a catastrophic weapon is found on the mysterious world of Acheron."
              description="A paragraph description of the DOW3 story. Acheron is a planet." />
          </p>
          <p className={css.war}>
            <FormattedMessage
              id="ui.branding.copy.DOW3_STORY_PANEL_BODY_SECOND"
              defaultMessage="With war raging and the planet under siege by the armies of greedy Ork warlord Gorgutz, ambitious Eldar seer Macha, and mighty Space Marine commander Gabriel Angelos, supremacy must ultimately be suspended for survival."
              description="A paragraph description of the DOW3 story. Gorgutz, Macha and Gabriel Angelos are characters. Ork, Eldar and Space Marines are names of factions." />
          </p>
        </div>
      </section>
    </LegacyCoveringScrollTransition>
  );
}

function ElitesPanel () {
  return (
    <LegacyScrollTransition css={css}>
      <section className={css['elites-panel']}>
        <div className={css['panel-content']} id="elites">
          <TrackViewOnVisible name="elites" />
          <h1 className={css.ElitesHeading}>
            <FormattedHTMLMessage
              id="ui.branding.heading.DOW3_ELITES_PANEL_ANIMATED_TAGLINE"
              defaultMessage={'<span class="{UNLEASH_CLASSNAME}">Unleash</span> <span class="{GIANTS_CLASSNAME}">the Giants</span>'}
              description="Animated tagline appearing on the home page. The two different <span>...</span> parts fade-in one after the other and should be roughly equal size. {UNLEASH_CLASSNAME} and {GIANTS_CLASSNAME} are used to give the different parts their animations and should remain in order."
              values={{
                UNLEASH_CLASSNAME: css.unleash,
                GIANTS_CLASSNAME: css.giants,
              }} />
          </h1>
          <hr className={`${css.short} ${css['align-left']}`} />
          <p className={css.ElitesDescription}>
            <FormattedMessage
              id="ui.branding.copy.DOW3_ELITES_PANEL_BODY"
              defaultMessage="Take control of towering war machines and tip the balance of battle in your favor with the biggest characters in Dawn of War history."
              description="Paragraph description of DOW3 elite units." />
          </p>
        </div>
        <div className={css['elite-1']} />
        <div className={css['elite-2']} />
        <div className={css['elite-3']} />
      </section>
    </LegacyScrollTransition>
  );
}

function EpicPanel () {
  return (
    <section className={css['epic-panel']}>
      <LegacyScrollTransition css={css}>
        <div className={`${css['panel-content']} ${css['sidebar-noclip']}`} style={{maxWidth: '1400px'}} id="epic">
          <div className={css.row}>
            <div className={`${css['asset-details']} ${css['asset-right']}`}>
              <TrackViewOnVisible name="epic" />
              <h1 className={css.EpicHeading}>
                <FormattedHTMLMessage
                  id="ui.branding.heading.DOW3_EPIC_PANEL_ANIMATED_TAGLINE"
                  defaultMessage={'<span class="{COLOSSAL_CSSNAME}">Colossal</span> <span class="{CLASHES_CSSNAME}">battles</span>'}
                  description="Animated tagline appearing on the home page. The two different <span>...</span> parts fade-in one after the other and should be roughly equal size. {COLOSSAL_CSSNAME} and {CLASHES_CSSNAME} are used to give the different parts their animations and should remain in order."
                  values={{
                    COLOSSAL_CSSNAME: css.colossal,
                    CLASHES_CSSNAME: css.clashes,
                  }} />
              </h1>
              <hr className={`${css.short} ${css['align-left']}`} />
              <p className={css.EpicDescription}>
                <FormattedMessage
                  id="ui.branding.copy.DOW3_EPIC_PANEL_BODY"
                  defaultMessage="Prepare for more epic action as you wage war with massive armies across landscapes full of volcanic terrain or on battlecruisers in space."
                  description="Paragraph description of the DOW3 battles." />
              </p>
            </div>
          </div>
        </div>
      </LegacyScrollTransition>
    </section>
  );
}

function CampaignPanel () {
  return (
    <section className={css['campaign-panel']}>
      <LegacyScrollTransition css={css}>
        <div className={css['panel-content']} id="campaign">
          <TrackViewOnVisible name="campaign" />
          <h1>
            <FormattedHTMLMessage
              id="ui.branding.heading.DOW3_CAMPAIGN_PANEL_ANIMATED_TAGLINE"
              defaultMessage={'<span class="{THREE_CLASSNAME}">Three</span><span class="{FACTION_CLASSNAME}">-faction</span> <span class="{CAMPAIGN_CLASSNAME}">campaign</span>'}
              description="Animated tagline appearing on the home page. The three different <span>...</span> parts fade-in one after the other and should be roughly equal size. {THREE_CLASSNAME}, {FACTION_CLASSNAME} and {CAMPAIGN_CLASSNAME} are used to give the different parts their animations and should remain in order."
              values={{
                THREE_CLASSNAME: css.three,
                FACTION_CLASSNAME: css.faction,
                CAMPAIGN_CLASSNAME: css.campaign,
              }} />
          </h1>
          <hr className={css.short} />
          <p>
            <FormattedMessage
              id="ui.branding.copy.DOW3_CAMPAIGN_PANEL_BODY"
              defaultMessage="This universe has no heroes or villains. Through alternating missions you'll soon learn the combat advantages of Space Marines, Orks, and Eldar."
              description="Paragraph description of the DOW3 campaign. Ork, Eldar and Space Marines are names of factions." />
          </p>
        </div>
      </LegacyScrollTransition>
    </section>
  );
}

function RacesPanel () {
  return (
    <LegacyScrollTransition css={css}>
      <section className={css.races} id="races">
        <TrackViewOnVisible name="races" />
        <div className={`${css['panel-content']} ${css['sidebar-noclip']}`}>
          <div className={`${css.ork} ${css.race}`} />
          <div className={`${css.eldar} ${css.race}`} />
          <div className={`${css.sm} ${css.race}`} />
        </div>
      </section>
    </LegacyScrollTransition>
  );
}

function DestructiveAbilitiesPanel () {
  return (
    <section className={css['destructive-abilities-panel']}>
      <LegacyScrollTransition css={css}>
        <div className={`${css['panel-content']} ${css['sidebar-noclip']}`} style={{maxWidth: '1400px'}} id="destructive-abilities">
          <div className={css.row}>
            <div className={`${css['asset-details']} ${css['asset-right']}`}>
              <TrackViewOnVisible name="destructive-abilities" />
              <h1 className={css.DestructiveHeading}>
                <FormattedHTMLMessage
                  id="ui.branding.heading.DOW3_DESTRUCTIVE_ABILITIES_PANEL_ANIMATED_TAGLINE"
                  defaultMessage={'<span class="{DESCTRUCTIVE_CLASSNAME}">Destructive</span> <span class="{ABILITIES_CLASSNAME}">abilities</span>'}
                  description="Animated tagline appearing on the home page. The two different <span>...</span> parts fade-in one after the other and should be roughly equal size. {DESCTRUCTIVE_CLASSNAME} and {ABILITIES_CLASSNAME} are used to give the different parts their animations and should remain in order."
                  values={{
                    DESCTRUCTIVE_CLASSNAME: css.destructive,
                    ABILITIES_CLASSNAME: css.abilities,
                  }} />
              </h1>
              <hr className={`${css.short} ${css['align-left']}`} />
              <p className={css.DestructiveDescription}>
                <FormattedMessage
                  id="ui.branding.copy.DOW3_DESTRUCTIVE_ABILITIES_PANEL_BODY"
                  defaultMessage="Devastate the battlefield with powerful super-abilities. Rain down total destruction on your enemies with each faction’s unique attack to counter your unsuspecting rivals."
                  description="Paragraph description of DOW3 super abilities." />
              </p>
            </div>
          </div>
        </div>
      </LegacyScrollTransition>
    </section>
  );
}

function CallUpElitesPanel () {
  return (
    <section className={css['call-your-elites-panel']}>
      <div className={css['elites-background']} />
      <LegacyScrollTransition css={css}>
        <div className={`${css['panel-content']} ${css['sidebar-noclip']}`} style={{maxWidth: '1400px'}} id="call-your-elites">
          <div className={css.row}>
            <div className={`${css['asset-details']} ${css['asset-right']}`}>
              <TrackViewOnVisible name="call-your-elites" />
              <h1>
                <FormattedHTMLMessage
                  id="ui.branding.heading.DOW3_CALL_UP_ELITES_PANEL_ANIMATED_TAGLINE"
                  defaultMessage={'<span class="{CALL_CLASSNAME}">Call</span> <span class="{UP_CLASSNAME}">up</span> <span class="{YOUR_CLASSNAME}">your</span> <span class="{ELITES_CLASSNAME}">elites</span>'}
                  description="Animated tagline appearing on the home page. The four different <span>...</span> parts get animated separately and should be roughly equal size. {CALL_CLASSNAME}, {UP_CLASSNAME}, {YOUR_CLASSNAME} and {ELITES_CLASSNAME} are used to give the different parts their animations and should remain in order. Elites are special hero units in the game."
                  values={{
                    CALL_CLASSNAME: css.call,
                    UP_CLASSNAME: css.up,
                    YOUR_CLASSNAME: css.your,
                    ELITES_CLASSNAME: css.elites,
                  }} />
              </h1>
              <hr className={`${css.short}`} />
              <p>
                <FormattedMessage
                  id="ui.branding.heading.DOW3_CALL_UP_ELITES_PANEL_BODY"
                  defaultMessage="Deploy all-new and powerful collectible elite squads, each with special abilities and bonuses, to unlock and develop new attacking strategies to conquer your foes."
                  description="Panel body that appears on the home page." />
              </p>
            </div>
          </div>
        </div>
      </LegacyScrollTransition>
    </section>
  );
}

function TrailerPanel () {
  return (
    <LegacyScrollTransition css={css}>
      <section className={css['re-trailer-panel']} id="retrailer">
        <div className={css['panel-bg']}>
          <picture>
            <source srcSet={contentLoader('trailer_preview-320.jpg')} type="image/jpeg" media="(max-height:320px)" />
            <source srcSet={contentLoader('trailer_preview-420.jpg')} type="image/jpeg" media="(min-height:321px) and (max-height:420px)" />
            <source srcSet={contentLoader('trailer_preview-460.jpg')} type="image/jpeg" media="(min-height:421px)" />
            <img src={contentLoader('trailer_preview-460.jpg')} className={css['bg-media']} />
          </picture>
          <video
            className={`${css['bg-media']} ${css.autoplay}`}
            loop
            muted
            preload="auto"
            autoPlay>
            <source src={contentLoader('trailer-preview-460.mp4', 'video')} type="video/mp4" />
          </video>
        </div>
        <div className={`${css['panel-content']} ${css.TrailerDescription}`}>
          <TrackViewOnVisible name="retrailer" />
          <FormattedHTMLMessage
            id="ui.branding.heading.DOW3_TRAILER_PANEL_ANIMATED_TAGLINE"
            defaultMessage={'<h1>Experience</h1> <h1 class="{RIGHT_CLASSNAME}">galactic warfare</h1>'}
            description="Animated tagline appearing on the home page. The two different <h1>...</h1> parts get animated separately. {RIGHT_CLASSNAME} is used to give that part a different animation and should be on the second <h1>."
            values={{
              RIGHT_CLASSNAME: css.right,
            }} />
          <a className={`trailer-launcher ${css.btn} ${css['button-accent']} ${css['fl-video']} ${css['launch-trailer']}`}>
            <span className={css['ico-play']} />
            <FormattedMessage
              id="ui.label.button.DOW3_WATCH_TRAILER"
              defaultMessage="watch trailer"
              description="Button that appears on the home page." />
          </a>
        </div>
      </section>
    </LegacyScrollTransition>
  );
}

class Trailer extends Component {
  openModal ({videoId, event}) {
    event.preventDefault();
    if (window.ga) {
      window.ga('send', 'event', 'Videos', 'Open', 'Main Trailer');
    }
    let player = new window.YT.Player('ytcontainer', {
      height: window.innerHeight,
      width: window.innerWidth,
      videoId,
      playerVars: {
        rel: 0,
        autoplay: 1,
        origin: window.location.origin,
        enablejsapi: 1,
        fs: 1,
      },
    });

    this.setState({modalOpen: true});
  }

  closeModal () {
    let youtubeContainer = document.getElementById('ytcontainer');
    let modal = youtubeContainer.parentElement;
    modal.removeChild(youtubeContainer);
    let newContainer = document.createElement('div');
    newContainer.id = 'ytcontainer';
    newContainer.className = 'ytcontainer';

    modal.appendChild(newContainer);

    this.setState({modalOpen: false});
  }

  render () {
    return (<div>
      <div onClick={() => this.closeModal()} className={`${css['modal-overlay']} ${(this.state && this.state.modalOpen) ? css['modal-open'] : ''}`} />
      <div className={`${this.props.css.trailer} ${this.props.css.modal} ${this.props.css.modal} ${(this.state && this.state.modalOpen) ? this.props.css['modal-open'] : ''}`}>
        <span onClick={() => this.closeModal()} className={this.props.css['modal-close-icon']}>✕</span>
        <div id="ytcontainer" className="ytcontainer" />
      </div>
    </div>);

  }
}



// WEBPACK FOOTER //
// ./apps/www.dawnofwar.com/src/views/home.jsx